import { api } from "./api";

const ONE_LAKH = 100_000;
const ONE_CRORE = 10_000_000;

/* Budget display string from the numeric fields.
 *
 * Used only for leads the CHATBOT creates: it sends budgetMin/budgetMax in
 * RUPEES, matching Property.priceMin/priceMax, and the display string is
 * generated from them. Leads entered in the admin keep free text as typed
 * ("50L", "1.2 Cr") -- see toApiPayload.
 *
 * Mirrors formatPriceRange in the WEBSITE's lib/format.ts, deliberately.
 * Note propertiesApi.js has a function of the same name that takes CRORES
 * and always prints "Cr" -- reusing it would render 50 lakh as "₹0.5 Cr".
 * Two formatters in this file's neighbourhood is not ideal; one shared
 * helper across both repos is the real fix, when there is time for it. */
function fmtRupees(n) {
  if (!n || n <= 0) return "";
  if (n < ONE_LAKH) return "₹" + Math.round(n / 1000) + " K";
  const trim = (x) => (x % 1 === 0 ? String(x) : x.toFixed(2).replace(/\.?0+$/, ""));
  if (n < ONE_CRORE) return "₹" + trim(n / ONE_LAKH) + " L";
  return "₹" + trim(n / ONE_CRORE) + " Cr";
}

export function formatBudgetRange(min, max) {
  const hasMin = typeof min === "number" && min > 0;
  const hasMax = typeof max === "number" && max > 0;
  if (hasMin && hasMax && min !== max) {
    return fmtRupees(min) + " - " + fmtRupees(max).replace("₹", "");
  }
  return fmtRupees(min) || fmtRupees(max);
}

/* Lead API wrapper + UI-shape adapter.

   The existing UI (LeadsList, Dashboard, LeadForm, LeadDetail) expects
   leads in the OLD admin's in-memory shape:
     - createdAt / followUpAt / lastStatusChangeAt are Date objects
     - phone = "+91 12345 67890" (constructed from mobile)
     - config is always an array
     - assignedTo is always an array

   The API returns:
     - dates as ISO strings
     - mobile as bare 10 digits
     - config / assignedTo / possession as JSON arrays

   `toUiLead()` adapts API → UI. `toApiPatch()` adapts UI patches → API.
   Both keep the screens unchanged. */

const formatPhone = (mobile) => {
  if (!mobile || mobile.length !== 10) return "";
  return "+91 " + mobile.slice(0, 5) + " " + mobile.slice(5);
};

const parseDate = (s) => (s ? new Date(s) : null);

/* Convert the API's lead record into the shape the UI expects. */
export function toUiLead(r) {
  if (!r) return r;
  return {
    id: r.id,
    name: r.name,
    mobile: r.mobile,
    /* Falls back to WhatsApp when there is no mobile. NRI leads arrive
       with a WhatsApp number only -- SMS to a foreign number is unreliable,
       so they skip OTP and have no Indian mobile. Without this the sales
       team sees an empty phone column and no way to reach them.

       The WhatsApp value is shown as stored: an international number
       already carries its country code, so formatPhone would be wrong --
       it prefixes +91 and only accepts exactly 10 digits. */
    phone: r.mobile ? formatPhone(r.mobile) : (r.whatsapp || ""),
    /* Derived once here so the list, the card and the detail view can badge
       these without each re-deriving the condition. */
    whatsappOnly: !r.mobile && !!r.whatsapp,
    whatsapp: r.whatsapp || "",
    email: r.email || "",
    occupation: r.occupation === "Self_employed" ? "Self-employed"
              : r.occupation === "Business_Owner" ? "Business Owner"
              : r.occupation || "Salaried",
    occupationOther: r.occupationOther || "",
    companyName: r.companyName || "",
    project: r.project || "",
    // Linked property's type (from the FK join) — drives per-type config labels
    // + the leads Type column/filter. Empty when the lead isn't linked.
    propertyType: r.property?.type || r.leadType || "",
    leadType: r.leadType || "",
    propertyName: r.property?.name || "",
    source: r.source === "Old_Data" ? "Old-Data" : r.source || "Other",
    sourceOther: r.sourceOther || "",
    status: r.status,
    config: Array.isArray(r.config) ? r.config : [],
    budget: r.budget || "",
    budgetMin: r.budgetMin ?? null,
    budgetMax: r.budgetMax ?? null,
    chatSummary: r.chatSummary || "",
    projectsViewed: Array.isArray(r.projectsViewed) ? r.projectsViewed : [],
    language: r.language || "",
    consentAt: r.consentAt || null,
    consentVersion: r.consentVersion || "",
    buyingPurpose: r.buyingPurpose === "Self_use" ? "Self-use"
                 : r.buyingPurpose === "Rental_Income" ? "Rental Income"
                 : r.buyingPurpose || "Self-use",
    reference: r.reference || "",
    address: r.address || "",
    remark: r.remark || "",
    note: r.note || "",
    possession: Array.isArray(r.possession) ? r.possession : ["Under Construction"],
    temperature: r.temperature || "warm",
    assignedTo: Array.isArray(r.assignedTo) ? r.assignedTo : [],
    followUpAt: parseDate(r.followUpAt),
    createdAt: parseDate(r.createdAt),
    updatedAt: parseDate(r.updatedAt),
    lastActivity: parseDate(r.lastActivity),
    lastStatusChangeAt: parseDate(r.lastStatusChangeAt),
    lastFollowUpAt: parseDate(r.lastFollowUpAt),
    recordTrust: r.recordTrust || "manual",
    // Optional nested data when fetched via /leads/:id
    notes: r.notes || undefined,
    statusHistory: r.statusHistory || undefined,
  };
}

/* Reverse mapping. UI labels → API enum strings. */
function toApiOccupation(label) {
  if (!label) return undefined;
  if (label === "Self-employed") return "Self_employed";
  if (label === "Business Owner") return "Business_Owner";
  return label;
}
function toApiSource(label) {
  if (label === "Old-Data") return "Old_Data";
  return label;
}
function toApiPurpose(label) {
  if (label === "Self-use") return "Self_use";
  if (label === "Rental Income") return "Rental_Income";
  return label;
}

/* Convert a UI-shape lead (e.g. from LeadForm submit) into an API
   create/update payload. */
export function toApiPayload(uiLead) {
  const p = {};
  const keys = [
    "name", "mobile", "whatsapp", "email", "companyName", "project",
    "leadType",
    "sourceOther", "occupationOther", "status", "config",
    "budget", "reference", "address", "remark", "note",
    "possession", "temperature", "assignedTo",
  ];
  for (const k of keys) {
    if (uiLead[k] === undefined) continue;
    p[k] = uiLead[k];
  }
  if (uiLead.occupation !== undefined) p.occupation = toApiOccupation(uiLead.occupation);
  if (uiLead.source !== undefined) p.source = toApiSource(uiLead.source);
  if (uiLead.buyingPurpose !== undefined) p.buyingPurpose = toApiPurpose(uiLead.buyingPurpose);
  if (uiLead.followUpAt !== undefined) {
    p.followUpAt = uiLead.followUpAt instanceof Date
      ? uiLead.followUpAt.toISOString()
      : uiLead.followUpAt;
  }
  if (uiLead.createdAt instanceof Date) p.createdAt = uiLead.createdAt.toISOString();

  /* Budget stays free text, as the sales team types it ("50L", "1.2 Cr").

     It was briefly two number inputs, so the chatbot could compare a lead's
     budget against listings -- but the chatbot never reads leads, it only
     writes them. The numeric fields earned nothing and cost a bug that wiped
     a stored budget, a 403 that blocked every employee save, and several
     leads now reading "Rs 0 K".

     budgetMin/budgetMax stay in the schema and in the read adapter: a lead
     the CHATBOT creates has real numbers, and the API generates its display
     string from them. Nothing in the admin writes them.

  /* chatSummary, projectsViewed, language, consentAt and consentVersion are
     deliberately NOT written from the admin. The chatbot owns them -- they
     are a record of what happened, and a form that posts every field would
     blank them on any ordinary edit. */
  // Strip empty whatsapp so backend stores null
  if (!p.whatsapp) delete p.whatsapp;
  /* Empty strings are SENT, not stripped.
  
     This used to delete any field the user had emptied, to avoid an
     empty-string vs null mismatch. But it conflated two different things:
     "never filled in" and "deliberately cleared". Deleting the key meant
     the API never heard about the clear, so the old value stayed -- a note
     could be set to "dummy" and saved, and then could not be emptied again.
  
     "" rather than null because the DTOs declare @IsString() without
     allowing null, so null would be rejected by validation. The API's own
     guard skips only undefined, so "" passes through and is stored.
  
     Consequence worth knowing: a cleared field now holds "" rather than
     NULL. Every display path checks truthiness, so both read as empty --
     but a raw `WHERE note IS NULL` query would not match cleared rows. */
  return p;
}

/* ─── API methods ─────────────────────────────────────────────────── */

export const leadsApi = {
  async list(filters = {}) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v === undefined || v === null || v === "" || v === "all") return;
      params.set(k, String(v));
    });
    const qs = params.toString();
    const r = await api.get(`/leads${qs ? `?${qs}` : ""}`);
    return {
      leads: (r.leads || []).map(toUiLead),
      total: r.total || 0,
      page: r.page || 1,
      pageSize: r.pageSize || 50,
    };
  },

  async getOne(id) {
    return toUiLead(await api.get(`/leads/${id}`));
  },

  async create(uiLead) {
    const r = await api.post("/leads", toApiPayload(uiLead));
    return toUiLead(r);
  },

  async update(id, patch) {
    const payload = toApiPayload(patch);
    console.log("[leadsApi.update] PATCH /leads/" + id + " payload:", payload);
    const r = await api.patch(`/leads/${id}`, payload);
    return toUiLead(r);
  },

  async delete(id) {
    return api.delete(`/leads/${id}`);
  },

  async addNote(leadId, body) {
    return api.post(`/leads/${leadId}/notes`, { body });
  },

  async bulk({ leadIds, action, assignedTo, status }) {
    return api.post("/leads/bulk", { leadIds, action, assignedTo, status });
  },

  async importCsv(csvText) {
    return api.post("/leads/import", { csv: csvText });
  },

  async exportCsv(filters = {}) {
    // Direct fetch — we need the response as a Blob to trigger download.
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v === undefined || v === null || v === "" || v === "all") return;
      params.set(k, String(v));
    });
    const url = `${api.base}/leads/export.csv${params.toString() ? `?${params}` : ""}`;
    const res = await fetch(url, { credentials: "include" });
    if (!res.ok) throw new Error(`Export failed: ${res.status}`);
    const blob = await res.blob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  },
};

/* ─── Active employees list — used by LeadForm "assignedTo" dropdown ── */
export async function listActiveEmployees() {
  const r = await api.get("/users");
  return (r.users || [])
    .filter((u) => u.active && u.role === "employee")
    .map((u) => ({
      id: u.id,
      name: u.name,
      email: u.email,
      // Compatibility shim for existing UI code that expects `profile`
      profile: { phone: "", whatsapp: "", joiningDate: null, role: "Employee" },
    }));
}
